/**
 * Bots API client — backs the dashboard Fleet page.
 *
 * Two entities here:
 *   - `BotRow` = a registered bot (hostname, version, live account count).
 *   - `EnrollCode` = an unused, unexpired code minted by the operator;
 *     burnt on successful /register.
 */

import { api } from "../api";

export interface BotRow {
  id: string;
  hostname: string | null;
  version: string | null;
  capabilities: string | null;
  registered_at: number;
  last_seen: number;
  status: "active" | "retired";
  /** Count of non-closed accounts pinned to this bot. Populated by
   *  `SELECT ... GROUP BY b.id` in the Worker. */
  assigned_account_count: number;
  /** Concurrent-awake-worker cap (migration 0052). Server-computed
   *  from reported_total_mem_mb on bot:hello, overridable per row. */
  max_concurrent_awake: number | null;
  max_concurrent_awake_override: number | null;
  reported_total_mem_mb: number | null;
  reported_free_mem_mb: number | null;
}

/** PATCH /api/bots/:id — operator-set cap override. Pass null to clear. */
export async function setBotCapOverride(
  id: string,
  override: number | null,
): Promise<{ ok: true; max_concurrent_awake_override: number | null }> {
  return api.patch(`/api/bots/${encodeURIComponent(id)}/cap`, {
    override,
  });
}

export interface MintedEnrollCode {
  code: string;
  label: string | null;
  createdAt: number;
  expiresAt: number;
  /** Public URL of the installer shell script. */
  installerUrl: string;
  /** Copy-paste-ready one-liner including the code + webhook URL. */
  oneLiner: string;
}

export interface EnrollCodeRow {
  code: string;
  label: string | null;
  created_by: string;
  created_at: number;
  expires_at: number;
  used_at: number | null;
  bound_bot_id: string | null;
}

export async function listBots(): Promise<BotRow[]> {
  const res = await api.get<{ bots: BotRow[] }>("/api/bots");
  return res.bots;
}

export async function retireBot(id: string): Promise<void> {
  await api.post<unknown>(`/api/bots/${encodeURIComponent(id)}/retire`, {});
}

export async function mintEnrollCode(label: string | null): Promise<MintedEnrollCode> {
  return api.post<MintedEnrollCode>("/api/bots/enroll-codes", { label });
}

export async function listEnrollCodes(): Promise<EnrollCodeRow[]> {
  const res = await api.get<{ codes: EnrollCodeRow[] }>("/api/bots/enroll-codes");
  return res.codes;
}

export async function cancelEnrollCode(code: string): Promise<void> {
  await api.del<unknown>(`/api/bots/enroll-codes/${encodeURIComponent(code)}`);
}

export async function assignAccountBot(accountSlug: string, botId: string): Promise<void> {
  await api.post<unknown>(`/api/accounts/${encodeURIComponent(accountSlug)}/assign-bot`, {
    botId,
  });
}
